<template>
  <div
    v-on-click-outside="onClickOutsideHandler"
    class="conditional-argument flex max-h-30 bg-white shadow-sm"
  >
    <div class="flex w-full flex-col bg-white px-4 py-4">
      <div
        v-if="isDraggable"
        class="flex h-full items-center justify-center border-r"
      >
        <icon-base
          :icon="IconDragHandle"
          width="12"
          height="20"
          :icon-color="UTIL_COLORS.DEFAULT"
        />
      </div>
      <div
        ref="setValueEl"
        class="mb-2 flex w-full flex-col justify-center space-y-1"
      >
        <div class="flex items-start justify-between">
          <div class="flex max-w-58 items-center">
            <span
              ref="attributeNameElement"
              v-tooltip="{
                content: getAttributeName(argument),
                onShow: () => isAttributeNameTruncated,
              }"
              class="truncate text-base text-gray-600"
            >
              {{ getAttributeName(argument) }}
            </span>
            <div
              v-if="serviceAttribute?.description"
              v-tooltip="serviceAttribute.description"
              class="conditional-argument__tooltip ml-2"
            >
              <icon-base :icon="IconInfo" />
            </div>
          </div>
          <div
            v-if="!isActiveTemplatePublished"
            class="flex min-w-15 justify-end space-x-3"
          >
            <edit-button v-if="hasSetValue" @click="handleEditValue" />
            <icon-base
              :icon="IconDelete"
              class="cursor-pointer text-disabled-medium"
              @click="emit('remove', argument)"
            />
          </div>
        </div>
      </div>
      <div
        v-if="hasSetValue"
        ref="attributeValueRef"
        v-tooltip="{
          content: attributeValue,
          onShow: () => isAttributeValueTruncated,
        }"
        data-cy="do-attribute-name-and-value"
        class="line-clamp-2 h-10 capitalize"
      >
        <div>{{ attributeValue }}</div>
        <div v-if="attributeContextValueString">
          {{ attributeContextValueString }}
        </div>
      </div>

      <span
        v-if="!isActiveTemplatePublished"
        data-cy="set-attribute-value-button"
        class="w-max cursor-pointer text-blue-600 select-none"
        @click="handleUpdateLinkClick"
      >
        {{ hasSetValue ? $t("COMMON.CHANGE_VALUE") : $t("COMMON.SET_VALUE") }}
      </span>

      <conditional-set-value
        v-if="currentAttributeData"
        ref="conditionalSetValueRef"
        v-model:conditional-model="conditionalModel"
        :data="currentAttributeData"
        :selected-values="selectedValues"
        :context="argument.context"
        class="z-30"
        :style="setValueModalPosition"
        @save="handleSave"
        @context:update-all="handleAllContextChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { AnalyticsEvent } from "@/enums/analytics";
import { isTextClamped } from "@/helpers/UI";
import {
  SENTILINK_MATCH_CONFIDENCES,
  SERVICE_CUSTOM,
  TIME_IN_BUSINESS_SERVICE_EXCEPTIONS,
  TYPE_NOT_NULL,
  TYPE_NULL,
  TYPE_RANGE,
  UTIL_COLORS,
} from "@/helpers/constants";
import { wordsFirstLetterToUpper } from "@/helpers/formatting";
import {
  haveNaicsRangeValues,
  typeIsNaicsCodeRange,
} from "@/helpers/orchestration";
import { checkValueType } from "@/helpers/scorecards";
import { useElementStatus } from "@/hooks/elements";
import {
  useAttributesOptions,
  useDataOrchestration,
  useDoActiveTemplateStatus,
} from "@/hooks/orchestration";
import contextToString from "@/lib/dataServices/contextToString";
import { useAnalyticsStore } from "@/stores/analytics";

import ConditionalSetValue from "./ConditionalSetValue.vue";
import IconDelete from "@/components/icons/IconDelete.vue";
import IconDragHandle from "@/components/icons/IconDragHandle.vue";
import IconInfo from "@/components/icons/IconInfo.vue";

import type { ConditionType } from "@/models/common";
import type {
  IActiveConfigPanelConditional,
  IAttribute,
  IConditionalSetValueModel,
  IConditionalSetWithContext,
  IContextSetValueModel,
  ICustomAttributeContext,
  IDataOrchestrationFlowCheckCondition,
  IDataService,
  IDataServices,
  IStep,
  NaicsRange,
} from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  argument: {
    type: Object as PropType<IDataOrchestrationFlowCheckCondition>,
    required: true,
  },
  attributeIndex: {
    type: Number as PropType<number>,
    required: true,
  },
  currentStep: {
    type: Object as PropType<IStep | null>,
    required: true,
  },
  sectionIndex: {
    type: Number as PropType<number>,
    required: true,
  },
  isDraggable: {
    type: Boolean,
    default: true,
  },
  isLast: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (eventName: "remove", argument: IDataOrchestrationFlowCheckCondition): void;
}>();

const CONDITIONAL_MODEL_DEFAULT: IConditionalSetValueModel = {
  type: "" as ConditionType,
  value: "",
};

const setValueEl = ref<HTMLElement | null>(null);
const conditionalSetValueRef = ref<HTMLElement | null>(null);
const { getters, commit } = useStore();
const { attributeOptions } = useAttributesOptions();
const { isActiveTemplatePublished } = useDoActiveTemplateStatus();
const { t } = useI18n();
const { isTextTruncated } = useElementStatus();
const { trackEvent } = useAnalyticsStore();
const { activeTemplate } = useDataOrchestration();

const conditionalModel = ref<IConditionalSetValueModel>({
  ...CONDITIONAL_MODEL_DEFAULT,
});

const attributeNameElement = ref<HTMLElement | null>(null);
const attributeValueRef = ref<HTMLElement | null>(null);
const currentAttributeData = ref<IAttribute | null>(null);

const isAttributeNameTruncated = isTextTruncated(attributeNameElement);

const isNullRelatedType = computed(() =>
  [TYPE_NULL, TYPE_NOT_NULL].includes(props.argument.conditions?.[0]?.type)
);

const selectedValues = computed(() => ({
  type: currentAttributeTypeKey.value,
  value: currentAttributeValue.value,
  secondValue: currentAttributeSecondValue.value,
}));

const setValueModalPosition = computed(() => {
  const conditionalAttributesParent: HTMLDivElement | null =
    document.querySelector("#conditionalParent");

  if (!conditionalAttributesParent) {
    return {};
  }

  const { offsetTop, offsetWidth } = conditionalAttributesParent;
  const parentPadding = 30;

  return {
    top: `${offsetTop}px`,
    right: `${offsetWidth + parentPadding}px`,
  };
});

const isAttributeValueTruncated = computed(() => {
  return isTextClamped(attributeValueRef.value);
});

const attributeValue = computed(() => {
  const value = `${currentAttributeType.value} ${currentAttributeValue.value}`;

  if (currentAttributeSecondValue.value) {
    return `${value} - ${currentAttributeSecondValue.value}`;
  }

  return value;
});

const currentAttributeType = computed(() => {
  if (props.argument.conditions?.length > 1) {
    return t("FIELD_DICTIONARY.range");
  }
  const type = props.argument.conditions?.[0]?.type;
  if (!type) {
    return "";
  }
  return wordsFirstLetterToUpper(
    type.replace(/flexible|case_insensitive/g, ""),
    "_"
  );
});

const currentAttributeTypeKey = computed(() => {
  if (props.argument.conditions?.length > 1) {
    return TYPE_RANGE;
  }
  return props.argument.conditions?.[0]?.type || "";
});

const serviceAttribute = computed<IAttribute | null>(
  () =>
    attributeOptions.value.find((option) =>
      props.argument.attribute === SERVICE_CUSTOM
        ? (option.context?.value as ICustomAttributeContext)?.id ===
          (props.argument.context?.value as ICustomAttributeContext).id
        : option.id === props.argument.attribute
    ) || null
);

const currentAttributeValue = computed(() => {
  if (
    props.argument.attribute.toLowerCase() ===
    "sentilink_ssn_completion_confidence_score_ssn"
  ) {
    return SENTILINK_MATCH_CONFIDENCES[
      props.argument.conditions?.[0]
        ?.value as keyof typeof SENTILINK_MATCH_CONFIDENCES
    ];
  }

  if (
    typeIsNaicsCodeRange(props.argument.conditions?.[0]?.type) &&
    haveNaicsRangeValues(props.argument.conditions?.[0])
  ) {
    // digits can get out of order, so we need to sort them
    const value = props.argument.conditions?.[0]?.value as NaicsRange;
    const digits = (value.digits || []).sort();
    const { from, through } = value || {
      from: "",
      through: "",
    };
    return `${from} - ${through} (${digits} ${t("COMMON.DIGITS")})`;
  }

  return formatValue(
    props.argument.conditions?.[0]?.value,
    getAttributeOptions()
  );
});

const hasSetValue = computed(
  () =>
    !!currentAttributeType.value &&
    (!!currentAttributeValue.value || isNullRelatedType.value)
);

const attributeContextValueString = computed(() =>
  hasSetValue.value && props.argument?.context?.value
    ? contextToString(props.argument.context.value)
    : ""
);

const currentAttributeSecondValue = computed(() =>
  formatValue(props.argument.conditions?.[1]?.value, getAttributeOptions())
);

const activeConditional = computed<IActiveConfigPanelConditional | null>(() => {
  return getters["orchestration/activeConfigPanelConditional"];
});

const services = computed<IDataServices>(
  () => getters["orchestration/activeServices"]
);

const getAttributeName = (argument: IDataOrchestrationFlowCheckCondition) => {
  return (
    serviceAttribute.value?.name ||
    wordsFirstLetterToUpper(argument.attribute, "_")
  );
};

const getAttributeOptions = (): Record<string, string> | null => {
  const service = [
    ...(services.value?.all_data_services || []),
    ...(services.value?.other || []),
  ].find((s) => s.title === props.currentStep?.title);
  if (!service) {
    return null;
  }
  const attribute = getAttribute(service);

  if (!attribute) {
    return null;
  }

  if (
    !!attribute.options &&
    Object.keys(attribute.options).length &&
    !Object.hasOwnProperty.call(
      attribute.options,
      "consumer_score_models_levels"
    ) &&
    !attribute.options?.month_picker_range &&
    !checkValueType(attribute.rules, "date") &&
    TIME_IN_BUSINESS_SERVICE_EXCEPTIONS.indexOf(
      props.argument
        .attribute as (typeof TIME_IN_BUSINESS_SERVICE_EXCEPTIONS)[number]
    ) === -1
  ) {
    return attribute.options;
  }
  return null;
};

const formatValue = (
  value: string | string[] | number | boolean | undefined | null | NaicsRange,
  options: Record<string, string> | null = null
) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "boolean") {
    return wordsFirstLetterToUpper(String(value));
  }
  if (Array.isArray(value)) {
    if (options !== null) {
      return value.map((element) => options[element]).join(", ");
    }
    return value.map((val) => wordsFirstLetterToUpper(val, "_")).join(", ");
  }
  if (options !== null && typeof value !== "object") {
    return options[value] ?? value;
  }
  return wordsFirstLetterToUpper(String(value), "_");
};

const getAttribute = (service: IDataService) => {
  if (service.data_service_details) {
    const allAttributeGroups = Object.values(
      service.data_service_details
    ).reduce<IAttribute[]>(
      (acc, curr) => [...acc, ...Object.values(curr).flat()],
      []
    );

    return Object.values(allAttributeGroups).find(
      (a) => a.id === props.argument.attribute
    );
  }

  return service.attributes?.find((a) => a.id === props.argument.attribute);
};

const handleSetValue = () => {
  const service = [
    ...(services.value.all_data_services || []),
    ...(services.value.other || []),
  ].find((s) => s.title === props.currentStep?.title);

  if (!service) {
    return;
  }

  const attribute = getAttribute(service);

  if (!attribute) {
    return;
  }

  currentAttributeData.value = { ...attribute };
  if (
    Object.keys(currentAttributeData.value.options || {}).length &&
    currentAttributeData.value.rules.value.includes("array")
  ) {
    conditionalModel.value = {
      ...conditionalModel.value,
      value: [],
    };
    if (currentAttributeSecondValue.value) {
      conditionalModel.value = {
        ...conditionalModel.value,
        secondValue: [],
      };
    }
  }
};

const handleEditValue = () => {
  const service = [
    ...(services.value.all_data_services || []),
    ...(services.value.other || []),
  ].find((s) => s.title === props.currentStep?.title);

  if (!service) {
    return;
  }

  const attribute = getAttribute(service);

  if (!attribute) {
    return;
  }

  currentAttributeData.value = { ...attribute };

  conditionalModel.value = {
    type: currentAttributeTypeKey.value,
    value: props.argument.conditions?.[0]?.value,
  };
  if (currentAttributeSecondValue.value) {
    conditionalModel.value = {
      ...conditionalModel.value,
      secondValue: props.argument.conditions?.[1]?.value as string,
    };
  }
};

const onClickOutsideHandler = [
  () => {
    currentAttributeData.value = null;
    conditionalModel.value = { ...CONDITIONAL_MODEL_DEFAULT };
  },
  { capture: false },
];

const handleAllContextChange = (context: IContextSetValueModel) => {
  commit("orchestration/updateConditionalContext", {
    stepId: props.currentStep?.id,
    outcome: activeConditional.value?.outcome,
    checkIndex: props.sectionIndex,
    context,
  });
};

const handleSave = (payload: IConditionalSetWithContext) => {
  currentAttributeData.value = null;

  if (!props.currentStep?.id || !activeConditional.value?.outcome) {
    return;
  }
  commit("orchestration/updateConditionalValue", {
    ...payload.conditional,
    stepId: props.currentStep?.id,
    outcome: activeConditional.value?.outcome,
    argument: props.argument,
    checkIndex: props.sectionIndex,
    context: payload.context,
  });
  trackEvent(AnalyticsEvent.DataOrchestrationConditionUpdated, {
    template_id: activeTemplate.value?.id,
    step_id: props.currentStep?.id,
    outcome: activeConditional.value?.outcome,
    attribute: props.argument.attribute,
    condition_type: payload.conditional.type,
  });
};

const handleUpdateLinkClick = () => {
  hasSetValue.value ? handleEditValue() : handleSetValue();
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.conditional-argument .conditional-argument__tooltip::before {
  @apply w-46;
}
</style>
